import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import GradientButton from './GradientButton';
const WakeUpCallSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  const handleGetInNow = () => {
    navigate('/nocode/onboarding');
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section id="wake-up-call" ref={sectionRef} className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-100px"
          }} transition={{
            duration: 0.6
          }} className="font-montserrat font-bold text-3xl md:text-4xl text-nocode-blue mb-4">
              How AI Nearly Took My Job… And How I Fought Back
            </motion.h2>
            
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-100px"
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="w-24 h-1 bg-nocode-gradient mx-auto mb-6" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: "-100px"
          }} transition={{
            duration: 0.6
          }}>
              <h3 className="font-montserrat font-semibold text-xl md:text-2xl text-gray-800 mb-4">
                The Day Everything Changed
              </h3>
              
              <p className="text-gray-600 mb-4">I'll never forget the day one of my client called me. &quot;We're downsizing our development team,&quot; he said. &quot;These AI tools are handling work that used to take weeks in just minutes.&quot;</p>
              
              <p className="text-gray-600 mb-4">
                As a developer with 10+ years of experience, I was shocked. My skills 
                that once commanded premium rates were being replaced by AI that anyone could use.
              </p>
              
              <p className="text-gray-600 mb-6">
                But instead of fighting against the inevitable, I decided to master these tools. 
                I discovered that combining my expertise with AI created something extraordinary—a 
                superpower that let me build in hours what used to take months.
              </p>
              
              <GradientButton onClick={handleGetInNow}>
                Future-Proof Yourself Now!
              </GradientButton>
            </motion.div>
            
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: "-100px"
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-nocode-gray rounded-xl overflow-hidden shadow-lg">
              <div className="grid grid-cols-2">
                <div className="p-6 border-r border-b border-gray-200">
                  <h4 className="font-montserrat font-semibold text-nocode-blue mb-2">Traditional Development</h4>
                  <ul className="text-gray-600 space-y-2">
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-red-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                      Weeks of coding
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-red-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                      Technical expertise required
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-red-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                      $10,000+ investment
                    </li>
                  </ul>
                </div>
                
                <div className="p-6 border-b border-gray-200">
                  <h4 className="font-montserrat font-semibold text-nocode-purple mb-2">AI-Powered Development</h4>
                  <ul className="text-gray-600 space-y-2">
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      Minutes, not weeks
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      No coding required
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      Fraction of the cost
                    </li>
                  </ul>
                </div>
                
                <div className="col-span-2 p-6 bg-gradient-to-r from-nocode-black to-nocode-purple text-white bg-slate-900">
                  <div className="flex flex-col items-center space-y-4 bg-gray-800">
                    <div className="flex flex-col items-center space-y-2">
                      <img src="/lovable-uploads/c61fb07a-4849-40ff-b9e6-fdbf8bea9b95.png" alt="Wole Akinloye" className="w-16 h-16 rounded-full object-cover border-2 border-white/20" />
                      <p className="font-montserrat font-medium text-sm">Wole Akinloye</p>
                    </div>
                    <p className="font-montserrat font-semibold text-center">
                      "I went from fearing AI would replace me to using it to 10x my output and income. 
                      Now I'm teaching others how to do the same."
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
};
export default WakeUpCallSection;